import { AxiosError } from 'axios';
import { queryCache, QueryConfig, useQuery } from 'react-query';

import { __DEV__ } from '~src/config';
import { loadFiles, LoadFilesParams } from '~src/services/api';
import { DbFile } from '~src/types/db';
import { useProjectId } from '../useProjectId';
import { useIsPath } from '~src/hooks/useIsPath';

// Tender folders API (inside tender) + project-level tender folders API (unassigned)
import {
  loadTenderTenderFoldersVersionedFiles,
  loadProjectTenderFoldersVersionedFiles,
} from '~src/services/api/folders';

type UseFilesParams = Omit<LoadFilesParams, 'projectId'>;

const parseTenderFoldersRoute = (pathname: string) => {
  // Examples:
  // 1) /projects/:pid/tenders/:tenderId/documents/tender-folders/:rootId/folders/:folderId
  // 2) /projects/:pid/tender-folders/:rootId/folders/:folderId
  const parts = pathname.split('/').filter(Boolean);

  const tendersIdx = parts.indexOf('tenders');
  const foldersIdx = parts.lastIndexOf('folders');

  const tenderId = tendersIdx >= 0 ? parts[tendersIdx + 1] : undefined;

  const tfIdx =
    parts.indexOf('tender-folders') >= 0
      ? parts.indexOf('tender-folders')
      : parts.indexOf('tender_folders');

  const rootFolderId = tfIdx >= 0 ? parts[tfIdx + 1] : undefined;
  const folderIdFromPath = foldersIdx >= 0 ? parts[foldersIdx + 1] : undefined;

  return { tenderId, rootFolderId, folderIdFromPath };
};

export const useFiles = (
  loadFilesParams: UseFilesParams,
  queryConfig: QueryConfig<DbFile[], AxiosError> & {
    enabled?: boolean;
    show_deleted?: boolean;
  } = {}
) => {
  const projectId = useProjectId();

  // Mode hints based on current route
  const isOnTenders = useIsPath('/tenders');
  const pathname = window.location.pathname;

  // Extract ids from tender-folders routes
  const { tenderId, rootFolderId, folderIdFromPath } = parseTenderFoldersRoute(
    pathname
  );

  // Detect "tender-folders route" even when not inside /tenders (unassigned tender folders)
  const isTenderFoldersRoute = pathname.includes('/tender-folders/');
  const isTenderFoldersInsideTender = isOnTenders && isTenderFoldersRoute;
  const isProjectTenderFolders = !isOnTenders && isTenderFoldersRoute;

  // Ensure we never call tender endpoints with undefined ids
  const canUseTenderFoldersInsideTender =
    isTenderFoldersInsideTender &&
    !!tenderId &&
    !!rootFolderId &&
    !!folderIdFromPath;

  const canUseProjectTenderFolders =
    isProjectTenderFolders && !!rootFolderId && !!folderIdFromPath;

  const { enabled, show_deleted, ...options } = queryConfig;
  // if enabled is undefined => treated as true
  const enabledOverwrite = enabled || enabled === undefined;

  // Archive "foldersReady" is tied to archive folders cache.
  // For tender folders endpoints we don't need this gating.
  const archiveFoldersReady = !!queryCache.getQueryData([
    'folders',
    {
      projectId: String(projectId),
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
  ]);

  // 3-mode queryKey:
  // - tender-files (inside tender)
  // - project-tender-files (unassigned tender folders)
  // - files (archive/default)
  const queryKey = canUseTenderFoldersInsideTender
    ? [
        'tender-files',
        {
          projectId: String(projectId),
          tender_id: String(tenderId),
          root_folder_id: String(rootFolderId),
          folder_id: String(folderIdFromPath),
          ...loadFilesParams,
          show_deleted,
        },
      ]
    : canUseProjectTenderFolders
    ? [
        'project-tender-files',
        {
          projectId: String(projectId),
          root_folder_id: String(rootFolderId),
          folder_id: String(folderIdFromPath),
          ...loadFilesParams,
          show_deleted,
        },
      ]
    : [
        'files',
        {
          projectId: String(projectId),
          show_deleted,
          ...loadFilesParams,
        },
      ];

  // 3-mode fetcher:
  const queryFn = canUseTenderFoldersInsideTender
    ? loadTenderTenderFoldersVersionedFiles
    : canUseProjectTenderFolders
    ? loadProjectTenderFoldersVersionedFiles
    : loadFiles;

  const filesQuery = useQuery<DbFile[], AxiosError>(
    queryKey as any,
    queryFn as any,
    {
      staleTime: 60 * 1000,
      enabled:
        enabledOverwrite &&
        !!projectId &&
        loadFilesParams.folderId !== 'undefined' &&
        // Archive needs archive folders cache ready; tender endpoints do not
        (canUseTenderFoldersInsideTender ||
          canUseProjectTenderFolders ||
          archiveFoldersReady),
      refetchOnWindowFocus: !__DEV__,
      ...options,
    }
  );

  return filesQuery;
};
